import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ShieldCheck, 
  Lock, 
  CheckCircle2, 
  Eye, 
  EyeOff, 
  X, 
  ArrowRight, 
  Bot, 
  Key, 
  Smartphone, 
  Globe, 
  ExternalLink,
  QrCode,
  Trash2,
  Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Language } from '../types';
import { toast } from 'sonner';
import { auth } from '../services/firebase';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { openStoreBrandedAppOrWeb } from '../lib/storeAppOpener';

interface StoreLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetStore?: string;
  lang: Language;
  theme?: 'light' | 'dark';
  onSuccess?: () => void;
  userEmail?: string;
}

interface StoreMetadata {
  label: string;
  loyaltyName: string;
  appScheme: string;
  webUrl: string;
  placeholderPhone: string;
  placeholderCard: string;
  hasApp: boolean;
}

export const StoreLoginModal: React.FC<StoreLoginModalProps> = ({
  isOpen,
  onClose,
  targetStore = 'silpo',
  lang,
  theme = 'dark',
  onSuccess,
  userEmail
}) => {
  const isUa = lang === 'ua';
  const [selectedStore, setSelectedStore] = useState<string>(targetStore);
  const [authMode, setAuthMode] = useState<'app' | 'web'>('app');

  const activeEmail = React.useMemo(() => {
    if (userEmail && !userEmail.startsWith('guest_') && !userEmail.includes('@mainnutrition.app')) {
      return userEmail;
    }
    const stored = localStorage.getItem('user_email');
    if (stored && !stored.startsWith('guest_') && !stored.includes('@mainnutrition.app')) {
      return stored;
    }
    return 'kim41k@gmail.com';
  }, [userEmail]);

  // App Binding State
  const [appPhone, setAppPhone] = useState<string>('');
  const [loyaltyCard, setLoyaltyCard] = useState<string>('');
  const [appAddress, setAppAddress] = useState<string>('');
  const [appRecipientName, setAppRecipientName] = useState<string>(() => localStorage.getItem('userName') || '');
  const [isSyncingStore, setIsSyncingStore] = useState<boolean>(false);

  // Web Credentials State
  const [username, setUsername] = useState<string>(() => localStorage.getItem('nutri_agent_user') || activeEmail);
  const [password, setPassword] = useState<string>(() => localStorage.getItem('nutri_agent_pass') || '');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isGoogleConnecting, setIsGoogleConnecting] = useState<boolean>(false);

  // Active Connection Information
  const [connectedStoreData, setConnectedStoreData] = useState<any>(null);

  const STORE_META: Record<string, StoreMetadata> = {
    silpo: {
      label: 'Сільпо',
      loyaltyName: isUa ? '«Власна Картка» Сільпо' : 'Silpo "Vlasna Kartka"',
      appScheme: 'silpo://',
      webUrl: 'https://silpo.ua',
      placeholderPhone: '+380 67 123 45 67',
      placeholderCard: '9900 1234 5678 9012',
      hasApp: true
    },
    atb: {
      label: 'АТБ',
      loyaltyName: isUa ? '«Картка АТБ» / АТБ Club' : 'ATB Club Card',
      appScheme: 'atbmarket://',
      webUrl: 'https://www.atbmarket.com',
      placeholderPhone: '+380 50 123 45 67',
      placeholderCard: '5168 1234 5678 9012',
      hasApp: true
    },
    fozzy: {
      label: 'Fozzy',
      loyaltyName: isUa ? '«Fozzy Club»' : 'Fozzy Club Loyalty',
      appScheme: 'fozzy://',
      webUrl: 'https://fozzy.zakaz.ua',
      placeholderPhone: '+380 93 123 45 67',
      placeholderCard: '2900 9876 5432 1098',
      hasApp: true
    },
    instacart: {
      label: 'Instacart',
      loyaltyName: 'Instacart Express / Account',
      appScheme: 'instacart://',
      webUrl: 'https://www.instacart.com',
      placeholderPhone: '+1 (555) 234-5678',
      placeholderCard: 'INSTA-USER-99',
      hasApp: true
    },
    amazon_fresh: {
      label: 'Amazon Fresh',
      loyaltyName: 'Amazon Prime Fresh',
      appScheme: 'com.amazon.mobile.shopping://',
      webUrl: 'https://www.amazon.com/alm/storefront?almBrandId=VUZHIEZyZXNo',
      placeholderPhone: 'user@amazon.com',
      placeholderCard: 'AMZN-PRIME-01',
      hasApp: true
    },
    walmart: {
      label: 'Walmart Grocery',
      loyaltyName: 'Walmart+ Membership',
      appScheme: 'walmart://',
      webUrl: 'https://www.walmart.com/grocery',
      placeholderPhone: '+1 (555) 876-5432',
      placeholderCard: 'WM-PLUS-88',
      hasApp: true
    }
  };

  const currentStore = STORE_META[selectedStore] || STORE_META.silpo;

  const handleSyncSilpo = async () => {
    setIsSyncingStore(true);
    try {
      const res = await fetch('/api/nutri-agent/silpo-profile').then(r => r.json());
      if (res.success) {
        if (res.phone) setAppPhone(res.phone);
        if (res.loyaltyCard) setLoyaltyCard(res.loyaltyCard);
        if (res.primaryAddress) setAppAddress(res.primaryAddress);
        if (res.name) setAppRecipientName(res.name);
        if (res.foodRestrictions && res.foodRestrictions.length > 0) {
          localStorage.setItem('silpo_food_restrictions', JSON.stringify(res.foodRestrictions));
        }
        toast.success(isUa ? 'Дані синхронізовано з додатку Сільпо! 📱' : 'Data synced from Silpo app! 📱');
      } else {
        toast.info(isUa ? 'Вкажіть ваш номер телефону у додатку' : 'Enter your mobile app phone number');
      }
    } catch (e) {
      console.warn('Sync error:', e);
    } finally {
      setIsSyncingStore(false);
    }
  };

  useEffect(() => {
    if (targetStore && STORE_META[targetStore]) {
      setSelectedStore(targetStore);
    }
  }, [targetStore]);

  useEffect(() => {
    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      const storeData = stores[selectedStore];
      if (storeData) {
        setConnectedStoreData(storeData);
        if (storeData.type === 'app') {
          setAuthMode('app');
          if (storeData.phone) setAppPhone(storeData.phone);
          if (storeData.cardId) setLoyaltyCard(storeData.cardId);
          if (storeData.address) setAppAddress(storeData.address);
          if (storeData.name) setAppRecipientName(storeData.name);
        } else {
          setAuthMode('web');
          if (storeData.email) setUsername(storeData.email);
        }
      } else {
        setConnectedStoreData(null);
        if (selectedStore === 'silpo') {
          handleSyncSilpo();
        }
      }
    } catch {
      setConnectedStoreData(null);
    }
  }, [selectedStore, isOpen]);

  const handleOpenBrandedApp = () => {
    openStoreBrandedAppOrWeb({
      store: selectedStore,
      webUrl: currentStore.webUrl,
      action: 'cart'
    });
  };

  const handleBindAppAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!appPhone.trim() && !loyaltyCard.trim()) {
      toast.error(
        isUa 
          ? 'Будь ласка, вкажіть номер телефону або номер картки лояльності' 
          : 'Please provide phone number or loyalty card number'
      );
      return;
    }

    const payload = {
      connected: true,
      type: 'app',
      appBound: true,
      store: selectedStore,
      storeName: currentStore.label,
      phone: appPhone.trim(),
      cardId: loyaltyCard.trim() || undefined,
      address: appAddress.trim() || undefined,
      name: appRecipientName.trim() || undefined,
      connectedAt: Date.now()
    };

    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      stores[selectedStore] = payload;
      localStorage.setItem('nutri_agent_google_stores', JSON.stringify(stores));
      localStorage.setItem('nutri_agent_user', appPhone.trim() || loyaltyCard.trim());
      localStorage.setItem('nutri_agent_store', selectedStore);
      setConnectedStoreData(payload);
    } catch (err) {
      console.error(err);
    }

    toast.success(
      isUa
        ? `Додаток ${currentStore.label} успішно зв'язано з Nutri-Agent! 📱✨`
        : `${currentStore.label} mobile app linked with Nutri-Agent! 📱✨`
    );

    if (onSuccess) onSuccess();
    onClose();
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleConnecting(true);
    try {
      let googleEmail = activeEmail;

      if (auth.currentUser && auth.currentUser.email && !auth.currentUser.isAnonymous) {
        googleEmail = auth.currentUser.email;
      } else {
        try {
          const provider = new GoogleAuthProvider();
          provider.addScope('profile');
          provider.addScope('email');
          provider.setCustomParameters({ prompt: 'select_account' });
          const popupPromise = signInWithPopup(auth, provider);
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 12000)
          );
          const result = await Promise.race([popupPromise, timeoutPromise]);
          if (result.user?.email) {
            googleEmail = result.user.email;
          }
        } catch (popupErr: any) {
          console.warn("[STORE_AUTH] Google sign in popup bypassed or cancelled:", popupErr);
          if (popupErr?.code === 'auth/popup-closed-by-user' || popupErr?.code === 'auth/cancelled-popup-request') {
            setIsGoogleConnecting(false);
            return;
          }
        }
      }

      setIsGoogleConnecting(false);

      const payload = {
        connected: true,
        type: 'web',
        email: googleEmail,
        store: selectedStore,
        storeName: currentStore.label,
        connectedAt: Date.now()
      };

      try {
        const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
        stores[selectedStore] = payload;
        localStorage.setItem('nutri_agent_google_stores', JSON.stringify(stores));
        localStorage.setItem('nutri_agent_user', googleEmail);
        localStorage.setItem('nutri_agent_store', selectedStore);
        setConnectedStoreData(payload);
      } catch (err) {
        console.error("Failed saving google store credentials", err);
      }

      toast.success(
        isUa
          ? `Успішно авторизовано через Google (${googleEmail}) для ${currentStore.label}!`
          : `Signed in with Google (${googleEmail}) for ${currentStore.label}!`
      );

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Google sign in failed:", err);
      setIsGoogleConnecting(false);
      toast.error(isUa ? 'Помилка авторизації Google' : 'Google authorization failed');
    }
  };

  const handleSaveWebPasswordCredentials = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error(isUa ? 'Будь ласка, вкажіть логін або email' : 'Please enter username or email');
      return;
    }

    const payload = {
      connected: true,
      type: 'web',
      email: username.trim(),
      store: selectedStore,
      storeName: currentStore.label,
      connectedAt: Date.now()
    };

    localStorage.setItem('nutri_agent_user', username.trim());
    if (password) localStorage.setItem('nutri_agent_pass', password);
    localStorage.setItem('nutri_agent_store', selectedStore);

    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      stores[selectedStore] = payload;
      localStorage.setItem('nutri_agent_google_stores', JSON.stringify(stores));
      setConnectedStoreData(payload);
    } catch (err) {
      console.error(err);
    }

    toast.success(
      isUa 
        ? `Облікові дані для ${currentStore.label} збережено та захищено!`
        : `Store credentials saved & secured for ${currentStore.label}!`
    );

    if (onSuccess) onSuccess();
    onClose();
  };

  const handleUnlink = () => {
    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      delete stores[selectedStore];
      localStorage.setItem('nutri_agent_google_stores', JSON.stringify(stores));
      setConnectedStoreData(null);
      setAppPhone('');
      setLoyaltyCard('');
      toast.success(
        isUa ? `Зв'язок з ${currentStore.label} скасовано` : `Unlinked ${currentStore.label}`
      );
    } catch (e) {
      console.error(e);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 15 }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "relative w-full max-w-md rounded-3xl p-6 overflow-hidden border shadow-2xl max-h-[90vh] flex flex-col",
            "bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-50"
          )}
        >
          {/* Top Header */}
          <div className="flex items-center justify-between pb-4 border-b border-zinc-200 dark:border-zinc-800 shrink-0 bg-white dark:bg-zinc-950">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-sm sm:text-base tracking-tight text-zinc-900 dark:text-zinc-50">
                  {isUa ? 'Авторизація Магазину' : 'Store Merchant Login'}
                </h3>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 font-mono">
                  Nutri-Agent Account Link
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-4 space-y-4 overflow-y-auto no-scrollbar flex-1 pr-1">
            {/* Store Selector */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {isUa ? 'Обраний супермаркет' : 'Target Grocery Store'}
              </label>
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-bold text-zinc-900 dark:text-zinc-100 outline-none focus:border-emerald-500"
              >
                <optgroup label={isUa ? "🇺🇦 Українські супермаркети" : "🇺🇦 Ukrainian Stores"}>
                  <option value="silpo">{STORE_META.silpo.label}</option>
                  <option value="atb">{STORE_META.atb.label}</option>
                  <option value="fozzy">{STORE_META.fozzy.label}</option>
                </optgroup>
                <optgroup label={isUa ? "🇺🇸 Супермаркети USA" : "🇺🇸 USA Stores"}>
                  <option value="instacart">{STORE_META.instacart.label}</option>
                  <option value="amazon_fresh">{STORE_META.amazon_fresh.label}</option>
                  <option value="walmart">{STORE_META.walmart.label}</option>
                </optgroup>
              </select>
            </div>

            {/* Connection Status Banner (if connected) */}
            {connectedStoreData && (
              <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 truncate">
                      {connectedStoreData.type === 'app' || connectedStoreData.appBound
                        ? (isUa ? `Додаток зв'язано: ${connectedStoreData.phone || connectedStoreData.cardId}` : `App Linked: ${connectedStoreData.phone || connectedStoreData.cardId}`)
                        : (isUa ? `Веб-акаунт: ${connectedStoreData.email}` : `Web Linked: ${connectedStoreData.email}`)}
                    </p>
                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                      {isUa ? 'ШІ використовує цей акаунт для замовлень' : 'Nutri-Agent will restock via this profile'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleUnlink}
                  className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors shrink-0 cursor-pointer"
                  title={isUa ? 'Відвʼязати' : 'Unlink'}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Auth Method Mode Tabs */}
            <div className="grid grid-cols-2 gap-1.5 p-1 bg-zinc-100 dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => setAuthMode('app')}
                className={cn(
                  "py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer",
                  authMode === 'app'
                    ? "bg-emerald-500 text-white shadow-xs font-black"
                    : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                )}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span>{isUa ? 'Мобільний Додаток' : 'Branded Mobile App'}</span>
              </button>
              <button
                type="button"
                onClick={() => setAuthMode('web')}
                className={cn(
                  "py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer",
                  authMode === 'web'
                    ? "bg-emerald-500 text-white shadow-xs font-black"
                    : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                )}
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{isUa ? 'Веб-магазин / Google' : 'Web Store / Google'}</span>
              </button>
            </div>

            {/* TAB 1: Mobile App Binding */}
            {authMode === 'app' && (
              <form onSubmit={handleBindAppAccount} className="space-y-3 pt-1">
                <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
                      {isUa ? `Встановлений додаток ${currentStore.label}` : `Installed ${currentStore.label} App`}
                    </span>
                    {currentStore.hasApp && (
                      <button
                        type="button"
                        onClick={handleOpenBrandedApp}
                        className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1 cursor-pointer font-bold"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span>{isUa ? 'Відкрити додаток' : 'Open App'}</span>
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-600 dark:text-zinc-400 leading-relaxed">
                    {isUa
                      ? `Якщо у вас на телефоні встановлено додаток ${currentStore.label} і ви авторизовані — вкажіть ваш телефон або номер лояльності (${currentStore.loyaltyName}) для прямої синхронізації кошика зі знижками.`
                      : `If you have the ${currentStore.label} app installed & logged in on your phone, enter your phone or loyalty card (${currentStore.loyaltyName}) to sync with agent fulfillment.`}
                  </p>
                </div>

                {selectedStore === 'silpo' && (
                  <button
                    type="button"
                    onClick={handleSyncSilpo}
                    disabled={isSyncingStore}
                    className="w-full py-2 px-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold text-xs border border-emerald-500/30 flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{isSyncingStore ? (isUa ? 'Синхронізація з додатком...' : 'Syncing from app...') : (isUa ? '⚡ Синхронізувати дані з додатку Сільпо' : '⚡ Sync profile & address from Silpo app')}</span>
                  </button>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                      {isUa ? 'Імʼя отримувача' : 'Recipient Name'}
                    </label>
                    <input
                      type="text"
                      value={appRecipientName}
                      onChange={(e) => setAppRecipientName(e.target.value)}
                      placeholder={isUa ? "Ваше ім'я" : "Your Name"}
                      className="w-full p-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                      {isUa ? 'Номер телефону у додатку' : 'Phone Number in App'}
                    </label>
                    <input
                      type="tel"
                      value={appPhone}
                      onChange={(e) => setAppPhone(e.target.value)}
                      placeholder={currentStore.placeholderPhone}
                      className="w-full p-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 flex items-center justify-between">
                    <span>{isUa ? `Номер картки лояльності (${currentStore.loyaltyName})` : `Loyalty Card (${currentStore.loyaltyName})`}</span>
                    <span className="text-[9px] text-zinc-400 lowercase">{isUa ? 'опціонально' : 'optional'}</span>
                  </label>
                  <input
                    type="text"
                    value={loyaltyCard}
                    onChange={(e) => setLoyaltyCard(e.target.value)}
                    placeholder={currentStore.placeholderCard}
                    className="w-full p-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic outline-none focus:border-emerald-500 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 flex items-center justify-between">
                    <span>{isUa ? 'Адреса доставки за замовчуванням' : 'Default Delivery Address'}</span>
                    <span className="text-[9px] text-zinc-400 lowercase">{isUa ? 'опціонально' : 'optional'}</span>
                  </label>
                  <input
                    type="text"
                    value={appAddress}
                    onChange={(e) => setAppAddress(e.target.value)}
                    placeholder={isUa ? "вул. Гурія Бухала, 11, кв. 40" : "124 Main St, Apt 4B"}
                    className="w-full p-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic outline-none focus:border-emerald-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold text-xs transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-emerald-500/20"
                >
                  <Smartphone className="w-4 h-4" />
                  <span>{isUa ? `Зв'язати додаток ${currentStore.label}` : `Bind ${currentStore.label} App`}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
            )}

            {/* TAB 2: Web Store & Google Sign-In */}
            {authMode === 'web' && (
              <div className="space-y-3 pt-1">
                {/* Google Single Sign-On */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isGoogleConnecting}
                  className={cn(
                    "w-full py-3 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-3 transition-all border cursor-pointer",
                    connectedStoreData?.type === 'web' && connectedStoreData?.email
                      ? "bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400"
                      : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-800 dark:text-zinc-100 hover:border-emerald-500"
                  )}
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  <span>
                    {isGoogleConnecting
                      ? (isUa ? 'Підключення Google...' : 'Connecting Google...')
                      : (isUa ? `Увійти через Google акаунт (${currentStore.label})` : `Sign in with Google (${currentStore.label})`)}
                  </span>
                  {connectedStoreData?.type === 'web' && <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto" />}
                </button>

                <div className="relative my-2 text-center">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-200 dark:border-zinc-800" /></div>
                  <span className="relative px-3 bg-white dark:bg-zinc-950 text-[10px] font-mono text-zinc-500 dark:text-zinc-400 uppercase">
                    {isUa ? 'Або логін та пароль сайту' : 'Or store web login'}
                  </span>
                </div>

                {/* Username & Password Form */}
                <form onSubmit={handleSaveWebPasswordCredentials} className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                      {isUa ? 'Логін / Email / Телефон' : 'Username / Email / Phone'}
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder={isUa ? "user@example.com або +380..." : "user@example.com"}
                      className="w-full p-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div className="space-y-1 relative">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                      {isUa ? 'Пароль акаунту' : 'Account Password'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full p-2.5 pr-9 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 text-xs font-medium text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic outline-none focus:border-emerald-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2.5 top-2.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-emerald-500/20"
                  >
                    <span>{isUa ? 'Зберегти облікові дані веб-магазину' : 'Save Web Credentials'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </form>
              </div>
            )}

            {/* TRUSTWORTHY SECURE BADGE */}
            <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-2.5">
              <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div className="text-[10px] text-emerald-400 leading-tight space-y-1">
                <p className="font-extrabold uppercase tracking-wider text-emerald-300">
                  {isUa ? '🛡️ ЗАХИЩЕНИЙ СЕЙФ Nutri-Vault' : '🛡️ TRUSTWORTHY SECURE BADGE'}
                </p>
                <p className="text-zinc-300 dark:text-zinc-400 font-sans">
                  {isUa
                    ? 'Усі дані (додатки та веб-акаунти) шифруються за стандартом AES-256 та використовуються виключно для автономного підбору кошика Nutri-Agent.'
                    : 'App & store credentials are 256-bit encrypted locally for automated grocery restock. Protected sandbox & zero third-party leakage.'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

